import React, { useState, useEffect, useRef } from 'react';

export const ArrangementView = ({ 
  tracks, 
  measures, 
  timeSignature, 
  zoom, 
  selectedTrackId, 
  onSelectTrack, 
  loopOn, 
  loopStart, 
  loopEnd, 
  setLoopStart, 
  setLoopEnd, 
  setLoopOn 
}) => {
  const beatsPerMeasure = parseInt(timeSignature.split('/')[0]);
  const cellWidth = 60 * zoom;
  const trackHeight = 60;
  
  // Loop region drag state
  const [isDraggingLoop, setIsDraggingLoop] = useState(false);
  const [loopDragType, setLoopDragType] = useState(null); // 'start', 'end', 'region'
  const [loopDragStartX, setLoopDragStartX] = useState(null);
  const loopRegionRef = useRef(null);
  
  // Handle loop region mouse down
  const handleLoopMouseDown = (e, type) => {
    e.stopPropagation();
    setIsDraggingLoop(true);
    setLoopDragType(type);
    setLoopDragStartX(e.clientX);
  };
  
  // Handle loop region mouse move
  useEffect(() => {
    if (!isDraggingLoop) return;
    
    const handleMouseMove = (e) => {
      const delta = e.clientX - loopDragStartX;
      const measureDelta = Math.round(delta / cellWidth);
      
      if (measureDelta === 0) return;
      
      if (loopDragType === 'start') {
        const newStart = Math.max(1, Math.min(loopEnd - 1, loopStart + measureDelta));
        setLoopStart(newStart);
      } else if (loopDragType === 'end') {
        // No upper limit - can extend infinitely
        const newEnd = Math.max(loopStart + 1, loopEnd + measureDelta);
        setLoopEnd(newEnd);
      } else if (loopDragType === 'region') {
        const regionLength = loopEnd - loopStart;
        let newStart = loopStart + measureDelta;
        let newEnd = loopEnd + measureDelta;
        
        if (newStart < 1) {
          newStart = 1;
          newEnd = 1 + regionLength;
        }
        // No upper limit for region drag either
        
        setLoopStart(newStart);
        setLoopEnd(newEnd);
      }
      
      setLoopDragStartX(e.clientX);
    };
    
    const handleMouseUp = () => {
      setIsDraggingLoop(false);
      setLoopDragType(null);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingLoop, loopDragType, loopDragStartX, loopStart, loopEnd, cellWidth, measures, setLoopStart, setLoopEnd]);
  
  // Click on timeline to set loop region
  const handleTimelineClick = (e) => {
    if (!loopRegionRef.current) return;
    const rect = loopRegionRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const measure = Math.floor(x / cellWidth) + 1;
    
    if (e.shiftKey && loopOn) {
      // Shift+click extends loop to this measure
      if (measure < loopStart) {
        setLoopStart(measure);
      } else if (measure > loopEnd) {
        setLoopEnd(measure);
      }
    } else if (e.altKey || e.metaKey) {
      // Alt/Cmd+click sets new loop region starting here (no upper limit)
      setLoopStart(measure);
      setLoopEnd(measure + 4);
      if (!loopOn) setLoopOn(true);
    }
  };
  
  return (
    <div className="flex-1 overflow-auto bg-[#0d0d0d]">
      {/* Loop Region Bar / Bar Counter */}
      <div 
        ref={loopRegionRef}
        className="sticky top-0 z-20 h-8 bg-[#1a1a1a] border-b border-[#444] flex relative cursor-pointer"
        onClick={handleTimelineClick}
        title="Alt+Click to set loop start, Shift+Click to extend"
      >
        {/* Measure/Bar markers */}
        {Array.from({ length: measures }).map((_, i) => (
          <div 
            key={i} 
            className={`border-r flex items-center justify-center font-mono ${
              (i + 1) % 4 === 0 ? 'border-[#555] bg-[#222]' : 'border-[#333]'
            }`}
            style={{ width: cellWidth, minWidth: cellWidth }}
          >
            <span className={`${cellWidth > 30 ? 'text-sm' : 'text-[10px]'} ${
              (i + 1) % 4 === 1 ? 'text-white font-bold' : 'text-gray-500'
            }`}>
              {i + 1}
            </span>
          </div>
        ))}
        
        {/* Loop Region Overlay */}
        {loopOn && (
          <div
            className="absolute top-0 h-full bg-yellow-500/40 border-l-2 border-r-2 border-yellow-500 flex items-center"
            style={{
              left: (loopStart - 1) * cellWidth,
              width: (loopEnd - loopStart) * cellWidth,
            }}
          >
            {/* Left handle */}
            <div
              className="absolute left-0 top-0 bottom-0 w-2 bg-yellow-500 cursor-ew-resize hover:bg-yellow-400 flex items-center justify-center"
              onMouseDown={(e) => handleLoopMouseDown(e, 'start')}
            >
              <div className="w-0.5 h-3 bg-yellow-800 rounded" />
            </div>
            
            {/* Center drag area */}
            <div
              className="flex-1 h-full cursor-move flex items-center justify-center"
              onMouseDown={(e) => handleLoopMouseDown(e, 'region')}
            >
              <span className="text-[10px] text-yellow-900 font-medium select-none">
                LOOP {loopStart}-{loopEnd}
              </span>
            </div>
            
            {/* Right handle */}
            <div
              className="absolute right-0 top-0 bottom-0 w-2 bg-yellow-500 cursor-ew-resize hover:bg-yellow-400 flex items-center justify-center"
              onMouseDown={(e) => handleLoopMouseDown(e, 'end')}
            >
              <div className="w-0.5 h-3 bg-yellow-800 rounded" />
            </div>
          </div>
        )}
      </div>
      
      {/* Timeline Header */}
      <div className="sticky top-6 z-10 h-8 bg-[#1a1a1a] border-b border-[#333] flex" style={{ marginLeft: 0 }}>
        {Array.from({ length: measures }).map((_, i) => (
          <div key={i} className="border-r border-[#333] flex items-center justify-center text-xs text-gray-500 font-mono" style={{ width: cellWidth }}>
            {i + 1}
          </div>
        ))}
      </div>
      
      {/* Track Lanes */}
      <div>
        {tracks.map((track, idx) => (
          <div
            key={track.id}
            onClick={() => onSelectTrack(track.id)}
            className={`flex border-b border-[#252525] cursor-pointer ${selectedTrackId === track.id ? 'bg-blue-900/20' : 'hover:bg-white/5'}`}
            style={{ height: trackHeight }}
          >
            {/* Track clips/notes visualization */}
            {Array.from({ length: measures }).map((_, m) => {
              const notesInMeasure = track.notes.filter(n => n.measure === m);
              return (
                <div key={m} className="border-r border-[#1a1a1a] relative" style={{ width: cellWidth }}>
                  {notesInMeasure.length > 0 && (
                    <div
                      className="absolute inset-1 rounded"
                      style={{ backgroundColor: track.color, opacity: 0.6 }}
                    >
                      <div className="text-[9px] text-white/80 px-1 truncate">{notesInMeasure.length} notes</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        
        {tracks.length === 0 && (
          <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
            Add tracks to see the arrangement
          </div>
        )}
      </div>
    </div>
  );
};

export default ArrangementView;
